import { getName } from 'country-list';
import { isNil, round } from 'lodash';
import { Event } from 'modules/events/types/eventsTypes';
import { ArtifactIdType } from 'shared/constants/artifactIdType';
import { formatCurrency, formatDate } from 'shared/functions';
import { artifactIdToRatingType } from 'shared/mappings/artifactIdTypeToRatingType';
import { parseDemandType, parseIndustryType, parseRatingType } from 'shared/parsers';
import { parseISO } from 'date-fns';
import omit from 'lodash/omit';
import {
  Portfolio,
  PortfolioEntity,
  PortfolioEvent,
  PortfolioInstrument,
  PortfolioFund,
  PortfolioRatingNews,
  PortfolioResearch,
  PortfolioUpload,
  InitialFilterRangeValue,
  FiltersData,
} from './types';

export const multiline = (firstLine: string, secondLine: string) => `${firstLine}\n${secondLine}`;

export const makePortfolioTableData = (portfolios: Portfolio[]) =>
  portfolios.map((item) => [
    [item.id, item.name, item.description ?? ''],
    formatDate(item.createdDate),
    item.noOfItems.entities,
    item.noOfItems.instruments,
    item.noOfItems.funds,
    item.coverage?.esg ?? 'N/A',
    item.coverage?.creditRating ?? 'N/A',
    item.coverage?.creditReview ?? 'N/A',
    [item.id, item.watchlistEnabled, item.isDedicated],
    null,
  ]);

export const makePortfolioItemsTableData = (portfolioItems: PortfolioEntity[]) => {
  const result = (portfolioItems ?? []).map((item) => {
    const idColumn = [item.id, item.artifactId, item];
    const nameColumn = [
      item.artifactId,
      item.name,
      artifactIdToRatingType[item.meta.artifactIdType ?? ArtifactIdType.ScopeOneId],
    ];
    const dataSource = item.meta.esgCustomScore
      ? 'ENTITY.DATA_SOURCE_USER'
      : 'ENTITY.DATA_SOURCE_SCOPE';

    const otherColumns = [
      // country,
      item.countryCode ? getName(item.countryCode) : 'N/A',
      // city
      item.meta?.sigCity ?? 'N/A',
      // postcode
      item.meta?.sigPostcode ?? 'N/A',
      // industry
      parseIndustryType(item.industry),
      // credit rating
      item,
      // credit review
      item,
      // ESG review
      item,
      // rating type
      item.meta?.storefrontRatingType ?? 'N/A',
      // demandType
      parseDemandType(item.meta?.demandType),
      // entityRating,
      item,
      // outlook
      item.meta?.storefrontRatingOutlook ?? 'N/A',
      // shortTermRating
      item,
      // probability,
      item.meta?.sigPOD ? `${item.meta.sigPOD}%` : 'N/A',
      // countryRisk,
      item.meta?.sigCountryRiskAssessment ?? 'N/A',
      // Renewal CREDIT RATING
      item,
      // environmental
      item,
      // social
      item,
      // governance
      item,
      // total
      item,
      // SENTIMENT environmental
      item,
      // SENTIMENT social
      item,
      // SENTIMENT governance
      item,
      // SENTIMENT total
      item,
      // CLIMATE_STRESS_TEST climateLoss
      item,
      // CLIMATE_STRESS_TEST physicalRiskLoss
      item,
      // CLIMATE_STRESS_TEST transitionRiskLoss
      item,
      // activity credit rating
      [item.activity?.creditRating, item.meta.storefrontOldScore, item.meta.storefrontScoreUpdate],
      // activity credit review
      [item.activity?.creditReview, item.meta.sigOldScore, item.meta.sigScoreUpdate],
      // activity ESG
      [item.activity?.esg, item.meta.esgOldScore, item.meta.esgScoreUpdate],
      // activity SENTIMENT
      [item.scores.sentifi?.status],
    ];

    return [idColumn, nameColumn, dataSource, ...otherColumns];
  });

  return result;
};

export const makeInstrumentsTableData = (portfolioItems: PortfolioInstrument[]) =>
  portfolioItems.map((item) => [
    [item.id, item.artifactId],
    item.name,
    item.isin,
    item.entityName,
    parseRatingType(item.meta.ratingType),
    parseDemandType(item.meta?.demandType),
    item.meta.rating ?? 'N/A',
    formatDate(item.coupon.maturityDate),
    !isNil(item.coupon?.couponPercent) ? `${round(item.coupon.couponPercent, 1)}%` : '-',
    formatCurrency(item.volume, item.currencyCode),
  ]);

export const makeFundsTableData = (portfolioItems: PortfolioFund[]) =>
  portfolioItems.map((item) => [
    item.isin,
    item.name,
    item.peergroupName,
    item.isin,
    item.rank,
    item.rating,
    item.ratingPoints,
    item.returnValue,
    item.risk,
    item.isAif,
    item.isQualitative,
  ]);

export const makeRatingNewsTableData = (portfolioItems: PortfolioRatingNews[]) =>
  portfolioItems.map((item) => [item.artifactId, item.title, formatDate(item.publicationDate)]);

export const makeResearchTableData = (portfolioItems: PortfolioResearch[]) =>
  portfolioItems.map((item) => [item.artifactId, item.title, formatDate(item.publicationDate)]);

export const makeEventsTableData = (portfolioItems: PortfolioEvent[]) =>
  portfolioItems.map((item) => [
    [item.id, item.artifactId],
    item.title,
    formatDate(item.eventDate, 'dd/MM/yyyy HH:MM a'),
    item.category,
    item.region,
    item.industry,
    {
      id: item.artifactId,
      connectionLink: item.meta.connectionLink,
      userRegistered: item.meta.userRegistered,
      eventDate: parseISO(item.eventDate),
    } as Event,
  ]);

export const makeUploadsTableData = (portfolioItems: PortfolioUpload[]) =>
  portfolioItems.map((item) => [
    item.id,
    [item.fileName, item.link, item.errorsLink], // name
    item.itemsType, // type
    item.results.uploadedItems ?? item.results.total, // uploaded items
    item.results.validationErrors, // validation errors
    item, // status
    item, // more actions
  ]);

export const rangeSliderValuesGenerator = (
  data: InitialFilterRangeValue,
  step: number = 0.01,
  unit: string = '',
) => {
  let { minimum } = data;
  const { maximum } = data;
  const valuesArray: { label: string; value: number }[] = [];
  while (minimum !== null && maximum !== null && minimum <= maximum) {
    const roundStep: number = 1 / step;
    const value: number = Math.round(minimum * roundStep) / roundStep;
    valuesArray.push({
      label: unit ? `${value}${unit}` : `${value}`,
      value,
    });
    minimum = value + step;
  }
  return valuesArray;
};

export const valueParser = (value: string[], unit: string = '') => {
  return value
    .filter((item) => {
      return item != null;
    })
    .map((item) => {
      return {
        label: unit ? item + unit : item,
        value: item,
      };
    });
};

export const minMaxValueInitialValues = (data: { label: string; value: number | string }[]) => [
  data[0]?.value,
  data[data.length - 1]?.value,
];

export const updateFilters = (data: FiltersData, name: keyof FiltersData, value: any) =>
  value ? { ...data, [name]: value } : omit(data, name);
